import { Suspense, type MutableRefObject, useEffect, useRef, useState } from 'react';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { useProperty, useSearch } from '@eti/providers';
import { TripType } from '@eti/schema-types';
import type { SearchOnResultPageQuery } from '@eti/schema-types';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import SearchTransition from '../../SearchTransition';
import userInfo from '../../../../../utils/userInfo';
import getFallbackUrl from '../../../../../utils/getFallbackUrl';
import { quickSortType } from '../../../../../constants/quickSortConstants';
import {
  SEARCH_QUERY,
  SEARCH_MORE_QUERY,
  NEARBY_DATE_PRICES_QUERY,
} from '../../../graphql/queries';
import ResultSkeleton from '../skeleton/ResultSkeleton';
import type { SearchFormValues } from '../../../../../common/search-form-panel/SearchFormNewContainer';
import { useResultPageContext } from '../contexts/ResultPageContext';
import SearchResults from '../SearchResults';

interface SearchResultsGraphqlContainerProps {
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
}

const SearchResultsGraphqlContainer = ({
  searchFormWatchRef,
  setValueRef,
}: SearchResultsGraphqlContainerProps) => {
  const {
    isInitialTripSelectionLoading,
    handleSearchData,
    searchData,
    setIsSearchLoading,
    setShouldRefetch,
    setShouldShowResultsLoader,
    setShowTripIsNotAvailableModal,
    shouldRefetch,
    shouldShowResultsLoader,
  } = useResultPageContext();

  const { p } = useProperty();
  const { searchVariables } = useSearch();
  const [shouldFetchMoreResults, setShouldFetchMoreResults] = useState(false);
  const [searchQueryData, setSearchQueryData] = useState<SearchOnResultPageQuery | null>(null);
  const isInitialRenderRef = useRef(true);
  const isSkeletonEnabled = p('Result.Skeleton.Enabled');
  const isNearbyDatesPricesEnabled = p('Result.Page.NearbyPrices.Enabled');
  const numOfNearbyDatesValues = 5;

  const [
    nearbyDatePrice,
    { data: nearbyDatesData, error: nearbyDatesError, loading: nearbyDatesLoading },
  ] = useLazyQuery(NEARBY_DATE_PRICES_QUERY, {
    variables: {
      origin: searchQueryData?.search?.routes?.[0]?.origin?.cityCode ?? '',
      destination: searchQueryData?.search?.routes?.[0]?.destination?.cityCode ?? '',
      numOfDays: numOfNearbyDatesValues,
      departureDate: searchQueryData?.search?.routes?.[0]?.departureDate ?? '',
      returnDate:
        searchQueryData?.search?.type === TripType.Return
          ? searchQueryData?.search?.routes?.[1]?.departureDate
          : null,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    loading: searchLoading,
    fetchMore,
    networkStatus,
    refetch,
  } = useQuery(SEARCH_QUERY, {
    variables: searchVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    onError: ({ graphQLErrors, networkError }) => {
      const fallbackUrl = getFallbackUrl();
      if (fallbackUrl) {
        window.location.href = fallbackUrl;
      } else {
        if (networkError) {
          throw new Error(`[Network error]: ${networkError}`);
        }
        const queryErrors = graphQLErrors.map((error) => error?.message).join(',');
        throw new Error(
          `Invalid response while fetching search results: ${queryErrors}. More info: ${JSON.stringify(
            userInfo(),
          )}`,
        );
      }
    },
    onCompleted: (response) => {
      setSearchQueryData(response);
      if (isNearbyDatesPricesEnabled) {
        nearbyDatePrice();
      }
    },
  });

  const isNearbyDatesLoading = searchLoading || nearbyDatesLoading;

  if (isInitialRenderRef.current) {
    isInitialRenderRef.current = false;
    if (searchQueryData) {
      refetch();
    }
  }

  useEffect(() => {
    if (searchQueryData && !searchLoading) {
      handleSearchData(searchQueryData);
      setShouldFetchMoreResults(false);
    }
    setIsSearchLoading(searchLoading);
  }, [searchQueryData, handleSearchData, searchLoading, setIsSearchLoading]);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [refetch, setShouldRefetch, shouldRefetch]);

  const handleCloseTripNotAvailableModal = () => {
    setShowTripIsNotAvailableModal(false);
    refetch();
  };

  useEffect(() => {
    const getOffset = () => searchData?.flights?.length || 0;
    const handleFetchMoreResults = () => {
      fetchMore({
        query: SEARCH_MORE_QUERY,
        variables: {
          ...searchVariables,
          offset: getOffset(),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            ...prev,
            search: {
              ...prev.search,
              flights: [
                ...(prev?.search?.flights || []),
                ...(fetchMoreResult?.search?.flights || []),
              ],
            },
          } as SearchOnResultPageQuery;
        },
      }).catch(console.error); // eslint-disable-line no-console
    };

    if (shouldFetchMoreResults) {
      handleFetchMoreResults();
      setShouldFetchMoreResults(false);
    }
  }, [fetchMore, shouldFetchMoreResults, searchData?.flights?.length, searchVariables]);

  if (!searchData || isInitialTripSelectionLoading) {
    if (isSkeletonEnabled) {
      return <ResultSkeleton />;
    }
    return <SearchTransition />;
  }

  const isNetworkStatusFetchMore = networkStatus === NetworkStatus.fetchMore;
  setShouldShowResultsLoader(searchLoading && !isNetworkStatusFetchMore);

  const defaultSortType = p('Result.QuickSort.Cheapest.Enabled')
    ? quickSortType.CHEAPEST
    : quickSortType.RECOMMENDED;
  const isQuickBarDropDownSelected = localStorage.getItem('isQuickBarDropDownSelected');
  if (!isQuickBarDropDownSelected) {
    if (
      shouldShowResultsLoader &&
      searchVariables?.filters?.length === 0 &&
      searchVariables.sortTypeCode === defaultSortType
    ) {
      if (isSkeletonEnabled) {
        return <ResultSkeleton />;
      }
      return <SearchTransition />;
    }
  }

  const nearbyDatePrices =
    !nearbyDatesError && nearbyDatesData && nearbyDatesData.nearbyDatePrices
      ? nearbyDatesData.nearbyDatePrices
      : [];

  const searchResultsPage =
    shouldShowResultsLoader && !isSkeletonEnabled ? (
      <SearchTransition />
    ) : (
      <SearchResults
        isNearbyDatesLoading={isNearbyDatesLoading}
        nearbyDatePrices={nearbyDatePrices}
        onCloseTripNotAvailableModal={handleCloseTripNotAvailableModal}
        searchFormWatchRef={searchFormWatchRef}
        setFetchMoreResults={setShouldFetchMoreResults}
        setValueRef={setValueRef}
      />
    );

  const FallBackComponent = () => {
    return isSkeletonEnabled ? <ResultSkeleton /> : <SearchTransition />;
  };
  return <Suspense fallback={<FallBackComponent />}>{searchResultsPage}</Suspense>;
};

export default SearchResultsGraphqlContainer;
